/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */
// @ts-nocheck

import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenSharpIcon from '@mui/icons-material/FullscreenSharp';
import FullscreenExitSharpIcon from '@mui/icons-material/FullscreenExitSharp';
import { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  OIEntity,
  OIScaleRange,
  PRICE_BOUNDS,
  STRIKE_ZOOM_BOUNDS,
  TraceLense,
  TraceStrikeBarType,
  ZoomData,
} from '../../types';
import {
  dayjs,
  getDateFormatted,
  getDefaultSlotProps,
  getQueryDate,
  isMarketOpenOnDate,
  marketDayAdd,
} from '../../util';
import { getApiBarType } from '../../util/oi';
import { SGTooltip } from '../../components/core';
import { InfoButton, SGSelect } from '../../components';
import { TraceColorSettings } from '../../components/trace/TraceColorSettings';
import { ChartSizingButton } from '../../components/shared/ChartSizingButton';
import { HideSupportStyle } from '../../components/shared/HideSupportStyle';
import {
  HEATMAP_FIRST_AVAILABLE_DATE,
  TRACE_LENSES,
  StrikeBarTypeLabels,
  TraceFiltersReadable,
  TraceShowChartType,
} from '../../config/oi';
import { ComboSymbol } from '../../config';
import { TraceVolGauge } from './TraceVolGauge';
import { useTraceCtx } from '../../components/trace/TraceContext';

type TraceCompactControlsProps = {
  timestamps: dayjs.Dayjs[] | number[];
  zoomData: ZoomData | undefined;
  resetZoom: () => void;
  setStrikeBarsLoading: (loading: boolean) => void;
  timestamp: dayjs.Dayjs | number | null;
  statsData: any;
  gammaAtLastPriceUninverted: number | undefined;
};

const OIENTITY_READABLE_ARRAY: [OIEntity, string][] = [
  [OIEntity.MARKET_MAKER, 'Market Makers'],
  [OIEntity.CUST, 'Customers'],
  [OIEntity.PROCUST, 'Pro Customers'],
  [OIEntity.BD, 'Broker Dealers'],
  [OIEntity.FIRM, 'Firms'],
];
const OIENTITY_READABLE = new Map<OIEntity, string>(OIENTITY_READABLE_ARRAY);

// At compact sizes we never show Both — the tile's too narrow for both panes
// side-by-side to be legible. User picks one or the other.
const CHART_TYPE_OPTIONS: [TraceShowChartType, string][] = [
  [TraceShowChartType.Heatmap, 'Heatmap'],
  [TraceShowChartType.StrikeBars, 'Strike Bars'],
];

// Settings dialog section — uppercase primary-colored label + consistently
// spaced children. Keeps every group of controls visually aligned.
const Section = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        color: 'primary.main',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        lineHeight: 1.4,
        mb: 1.5,
      }}
    >
      {label}
    </Typography>
    <Stack gap={2}>{children}</Stack>
  </Box>
);

export const TraceCompactControls = ({
  timestamps,
  zoomData,
  resetZoom,
  setStrikeBarsLoading,
  timestamp,
  statsData,
  gammaAtLastPriceUninverted,
}: TraceCompactControlsProps) => {
  const theme = useTheme();
  const defaultSlotProps = getDefaultSlotProps(theme);
  const {
    config,
    updateConfig,
    isMobile,
    intradayDate,
    intradaySym,
    selectedLense,
    fullscreen,
    setFullscreen,
    showChartType: userShowChartType,
    setShowChartType,
    showInternalLenses,
    userTraceV2Enabled,
    mode,
  } = useTraceCtx();
  // Compact UI doesn't expose `Both` — clamp the underlying user preference
  // for display purposes (Both → Heatmap). User's actual selection stays
  // intact in context, so resizing back to desktop brings Both back.
  const showChartType =
    userShowChartType === TraceShowChartType.Both
      ? TraceShowChartType.Heatmap
      : userShowChartType;

  // On mobile we strip everything non-essential; tablet keeps a bit more.
  const isXs = isMobile;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const closeSettings = () => setSettingsOpen(false);

  const queryDate = getQueryDate(true);
  const afterProjectionEODUpdates =
    queryDate >= queryDate.hour(17).minute(30).second(0).millisecond(0);
  const queryDateBodTs = queryDate.startOf('day').valueOf();
  const maxQueryDate = useMemo(
    () =>
      userTraceV2Enabled
        ? marketDayAdd(queryDate, afterProjectionEODUpdates ? 6 : 5)
        : dayjs(getQueryDate(true).format()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryDateBodTs, afterProjectionEODUpdates, userTraceV2Enabled],
  );

  const selectedTimestampIndex = useMemo(() => {
    let idx = timestamps.length - 1;
    for (let i = timestamps.length - 1; i >= 0; i--) {
      if ((timestamp as any)?.isSame?.(timestamps[i], 'second')) {
        idx = i;
        break;
      }
    }
    return idx;
  }, [timestamps, timestamp]);

  const isLatestTimestamp = selectedTimestampIndex === timestamps.length - 1;

  const handleStrikeBarTypeChange = (newType: TraceStrikeBarType) => {
    const oldApi = getApiBarType(config.strikeBarType);
    const newApi = getApiBarType(newType);
    if (newApi !== oldApi && newType !== TraceStrikeBarType.NONE) {
      setStrikeBarsLoading(true);
    }
    updateConfig({ strikeBarType: newType });
  };

  const hiroSym = config.hiroSyms?.[intradaySym] ?? intradaySym;

  return (
    <Stack
      direction="row"
      gap="6px"
      alignItems="center"
      flexWrap="wrap"
      sx={{
        px: '8px',
        py: '4px',
        width: 1,
        justifyContent: 'space-between',
      }}
    >
      {/* Primary controls — always visible */}
      <Stack direction="row" gap="6px" alignItems="center" flexWrap="wrap">
        <SGSelect
          key="compact-lense"
          label="Lens"
          multiple={false}
          value={selectedLense}
          setter={(v: any) => updateConfig({ lense: v })}
          options={
            showInternalLenses
              ? Object.values(TraceLense)
                  .filter((k) => typeof k != 'string')
                  .sort((a: any, b: any) => a - b)
              : TRACE_LENSES
          }
          optionsTextTransform={(f: any) => TraceFiltersReadable.get(f) ?? ''}
          sx={{ height: '32px', minWidth: isXs ? '90px' : '120px' }}
        />

        {!isXs && (
          <SGSelect
            key="compact-chart-type"
            label="Chart"
            multiple={false}
            value={showChartType}
            setter={(v: any) => setShowChartType(v as TraceShowChartType)}
            options={CHART_TYPE_OPTIONS.map(([v]) => v)}
            optionsTextTransform={(v: any) =>
              CHART_TYPE_OPTIONS.find(([val]) => val === v)?.[1] ?? ''
            }
            sx={{ height: '32px', minWidth: '95px' }}
          />
        )}

        {!isXs && (
          <TraceVolGauge
            statsData={statsData}
            gammaAtLastPriceUninverted={gammaAtLastPriceUninverted}
            parquetKey={config.mktActor}
            width={60}
            height={38}
            fontSize={10}
          />
        )}
      </Stack>

      {/* Trailing cluster — zoom-out, settings, colors, fullscreen */}
      <Stack direction="row" gap="2px" alignItems="center">
        {zoomData != null && (
          <SGTooltip title="Zoom out">
            <Button
              className="canvas-no-drag"
              size="small"
              onClick={resetZoom}
              sx={{ minWidth: 0, fontSize: 11, px: '6px' }}
            >
              Reset zoom
            </Button>
          </SGTooltip>
        )}

        {/* Unified settings — MUI Dialog handles nested portaled menus
            (Select, RadioGroup) correctly and centers on screen, with full-
            screen takeover on mobile. */}
        <SGTooltip title="TRACE Settings">
          <IconButton
            className="canvas-no-drag"
            size="small"
            color="primary"
            onClick={() => setSettingsOpen(true)}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </SGTooltip>

        <Dialog
          className="canvas-no-drag"
          open={settingsOpen}
          onClose={closeSettings}
          fullScreen={isMobile}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: isMobile ? 0 : 2 },
          }}
        >
          {/* Dialog only mounts children while open, so the global style
              hiding the support widget is scoped to while the modal is up. */}
          <HideSupportStyle />
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              px: 2.5,
            }}
          >
            <Typography component="span" sx={{ fontSize: 16, fontWeight: 600 }}>
              TRACE Settings
            </Typography>
            <IconButton
              aria-label="Close settings"
              onClick={closeSettings}
              size="small"
              sx={{ ml: 1 }}
              color="primary"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider
            sx={{
              borderColor: (t) => alpha(t.palette.primary.main, 0.25),
            }}
          />
          <DialogContent sx={{ p: 2.5 }}>
            <Stack
              gap={3}
              divider={
                <Divider
                  flexItem
                  sx={{
                    borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                  }}
                />
              }
            >
              <Section label="Chart">
                <ButtonGroup size="small" sx={{ width: '100%' }}>
                  {CHART_TYPE_OPTIONS.map(([value, label]) => (
                    <Button
                      key={value}
                      variant={
                        showChartType === value ? 'contained' : 'outlined'
                      }
                      onClick={() => setShowChartType(value)}
                      sx={{ fontSize: 11, textTransform: 'none', flex: 1 }}
                    >
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Section>

              <Section label="Date & Market">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={['day']}
                    value={intradayDate}
                    onChange={(newValue: Dayjs | null) => {
                      if (newValue && newValue.isValid()) {
                        updateConfig({ date: getDateFormatted(newValue) });
                      }
                    }}
                    minDate={HEATMAP_FIRST_AVAILABLE_DATE}
                    maxDate={maxQueryDate}
                    shouldDisableDate={(d: Dayjs) => !isMarketOpenOnDate(d)}
                    format="YYYY-MM-DD"
                    slotProps={{
                      ...defaultSlotProps,
                      textField: {
                        ...defaultSlotProps.textField,
                        size: 'small',
                        style: { width: '100%' },
                      },
                    }}
                  />
                </LocalizationProvider>

                {userTraceV2Enabled && (
                  <SGSelect
                    label="Market Participant"
                    multiple={false}
                    value={config.mktActor ?? OIEntity.MARKET_MAKER}
                    setter={(v: any) => updateConfig({ mktActor: v })}
                    options={OIENTITY_READABLE_ARRAY.map(([v]) => v)}
                    optionsTextTransform={(o: any) =>
                      OIENTITY_READABLE.get(o as OIEntity)
                    }
                    sx={{ height: '36px', width: '100%' }}
                  />
                )}

                {userTraceV2Enabled && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={config.inverted}
                        onChange={(e) =>
                          updateConfig({ inverted: e.target.checked })
                        }
                      />
                    }
                    label={<Typography fontSize={12}>Inverted</Typography>}
                  />
                )}

                {!isLatestTimestamp && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={config.filterLinesBasedOnTimeSlider}
                        onChange={(e) =>
                          updateConfig({
                            filterLinesBasedOnTimeSlider: e.target.checked,
                          })
                        }
                      />
                    }
                    label={
                      <Typography fontSize={12}>
                        Limit lines to selected time
                      </Typography>
                    }
                  />
                )}
              </Section>

              <Section label="Strike Bars">
                <SGSelect
                  label="Strike Bar Type"
                  multiple={false}
                  value={config.strikeBarType}
                  setter={(v: any) => handleStrikeBarTypeChange(v)}
                  options={[
                    TraceStrikeBarType.GAMMA,
                    TraceStrikeBarType.OI,
                    TraceStrikeBarType.OI_NET,
                    TraceStrikeBarType.NONE,
                  ]}
                  optionsTextTransform={(s: any) =>
                    StrikeBarTypeLabels.get(s) ?? String(s)
                  }
                  sx={{ height: '36px', width: '100%' }}
                />

                <Box>
                  <Typography fontSize={12}>
                    Strike Zoom (
                    {STRIKE_ZOOM_BOUNDS.indexOf(config.strikeZoom) + 1}/
                    {STRIKE_ZOOM_BOUNDS.length})
                  </Typography>
                  <Slider
                    size="small"
                    marks
                    value={STRIKE_ZOOM_BOUNDS.indexOf(config.strikeZoom)}
                    min={0}
                    max={STRIKE_ZOOM_BOUNDS.length - 1}
                    onChange={(_e, idx) => {
                      if (!Array.isArray(idx)) {
                        updateConfig({ strikeZoom: STRIKE_ZOOM_BOUNDS[idx] });
                      }
                    }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={config.showGexZeroDte}
                      onChange={(e) =>
                        updateConfig({ showGexZeroDte: e.target.checked })
                      }
                    />
                  }
                  label={<Typography fontSize={12}>0DTE only</Typography>}
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={config.strikeBarsTrackerEnabled}
                      onChange={(e) =>
                        updateConfig({
                          strikeBarsTrackerEnabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label={<Typography fontSize={12}>Tracker dots</Typography>}
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={config.strikeBarsHistoryEnabled}
                      onChange={(e) =>
                        updateConfig({
                          strikeBarsHistoryEnabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography fontSize={12}>
                      Daily min/max whiskers
                    </Typography>
                  }
                />
              </Section>

              <Section label="Heatmap">
                <Box>
                  <Typography fontSize={12}>
                    Price Zoom ({PRICE_BOUNDS.indexOf(config.priceZoom) + 1}/
                    {PRICE_BOUNDS.length})
                  </Typography>
                  <Slider
                    size="small"
                    marks
                    value={PRICE_BOUNDS.indexOf(config.priceZoom)}
                    min={0}
                    max={PRICE_BOUNDS.length - 1}
                    onChange={(_e, idx) => {
                      if (!Array.isArray(idx)) {
                        updateConfig({ priceZoom: PRICE_BOUNDS[idx] });
                      }
                    }}
                    disabled={zoomData != null}
                  />
                </Box>

                <SGSelect
                  label="Candle Duration"
                  multiple={false}
                  value={config.candleDuration}
                  setter={(v: any) => updateConfig({ candleDuration: v })}
                  options={[60, 5 * 60, 15 * 60]}
                  optionsTextTransform={(d: any) => `${d / 60}m`}
                  sx={{ height: '36px', width: '100%' }}
                />

                <FormControl>
                  <Typography fontSize={12}>Scale Range</Typography>
                  <RadioGroup
                    row
                    value={config.scaleRange}
                    onChange={(e) =>
                      updateConfig({
                        scaleRange: e.target.value as OIScaleRange,
                      })
                    }
                  >
                    {Object.values(OIScaleRange).map((v) => (
                      <FormControlLabel
                        key={v}
                        value={v}
                        control={<Radio size="small" />}
                        label={<Typography fontSize={11}>{v}</Typography>}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={config.showKeyLevels}
                      onChange={(e) =>
                        updateConfig({ showKeyLevels: e.target.checked })
                      }
                    />
                  }
                  label={<Typography fontSize={12}>Key Levels</Typography>}
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={config.showColorScale}
                      onChange={(e) =>
                        updateConfig({ showColorScale: e.target.checked })
                      }
                    />
                  }
                  label={<Typography fontSize={12}>Color Scale</Typography>}
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={config.showContourLines}
                      onChange={(e) =>
                        updateConfig({ showContourLines: e.target.checked })
                      }
                    />
                  }
                  label={<Typography fontSize={12}>Contour Lines</Typography>}
                />
              </Section>

              <Section label="HIRO & Stats">
                <FormControl>
                  <Typography fontSize={12}>HIRO Symbol</Typography>
                  <RadioGroup
                    row
                    value={hiroSym}
                    onChange={(e) =>
                      updateConfig({
                        hiroSyms: {
                          ...(config.hiroSyms ?? {}),
                          [intradaySym]: e.target.value,
                        },
                      })
                    }
                  >
                    <FormControlLabel
                      value={ComboSymbol.SPX_EQUITIES}
                      control={<Radio size="small" />}
                      label={
                        <Typography fontSize={11}>
                          {ComboSymbol.SPX_EQUITIES}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value={ComboSymbol.SPX}
                      control={<Radio size="small" />}
                      label={
                        <Typography fontSize={11}>{ComboSymbol.SPX}</Typography>
                      }
                    />
                    <FormControlLabel
                      value={''}
                      control={<Radio size="small" />}
                      label={<Typography fontSize={11}>None</Typography>}
                    />
                  </RadioGroup>
                </FormControl>

                <Box>
                  <Typography fontSize={12}>Stats Lookback</Typography>
                  <ButtonGroup size="small" sx={{ width: '100%' }}>
                    {[30, 60, 90].map((p) => (
                      <Button
                        key={p}
                        variant={
                          p === config.statsLookbackDays
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => updateConfig({ statsLookbackDays: p })}
                        sx={{ fontSize: 11, textTransform: 'none', flex: 1 }}
                      >
                        {p}d
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>

                <InfoButton
                  articleKey={`TRACE-${TraceLense[selectedLense]}`}
                  tooltipTitle="Learn about the current lens"
                />
              </Section>
            </Stack>
          </DialogContent>
          <Divider
            sx={{
              borderColor: (t) => alpha(t.palette.primary.main, 0.25),
            }}
          />
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            {/* Settings persist live on every change — this is just a clear
                "I'm done" affordance so users don't have to hunt for the X. */}
            <Button
              onClick={closeSettings}
              variant="contained"
              color="primary"
              sx={{ textTransform: 'none', px: 3 }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        <TraceColorSettings />

        {/* Fullscreen button — only in standalone mode; Canvas has its own */}
        {mode === 'standalone' && (
          <ChartSizingButton
            onClick={() => setFullscreen(!fullscreen)}
            icon={
              fullscreen ? <FullscreenExitSharpIcon /> : <FullscreenSharpIcon />
            }
            title={fullscreen ? 'Minimize TRACE' : 'Maximize TRACE'}
            buttonSx={{ height: '32px', marginTop: '-1px' }}
          />
        )}
      </Stack>
    </Stack>
  );
};
